import { useState } from "react";
import { PlusIcon, UserIcon, UsersIcon } from "lucide-react";

import {
  Column,
  EmptyState,
  ErrorText,
  LoadingIndicator,
  Row,
} from "@/components";
import { IntakeTab } from "@/components/companies/shared/IntakeTab";
import { IntakeTabBar } from "@/components/companies/shared/IntakeTabBar";
import { StakeholderForm } from "@/components/companies/shared/StakeholderForm";
import { StakeholderReadOnly } from "@/components/companies/shared/StakeholderReadOnly";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useCreateAddJobStakeholder } from "@/hooks/createAdds/useCreateAddJobStakeholder";
import { useCreateAddStakeholder } from "@/hooks/createAdds/useCreateAddStakeholder";
import { useDeleteRemoveJobStakeholder } from "@/hooks/deleteRemoves/useDeleteRemoveJobStakeholder";
import { useGetJobStakeholders } from "@/hooks/queries/useGetJobStakeholders";
import { useGetStakeholders } from "@/hooks/queries/useGetStakeholders";
import { EMPTY_PROGRESS } from "@/lib/constants";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { Progress, Stakeholder } from "@/types";

export const JobStakeholders = ({
  isEditing,
  setIsEditing,
  progressByStakeholderId,
}: {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  progressByStakeholderId: Record<number, Progress>;
}) => {
  /* Database CRUD Operations */
  // Create
  const { mutate: createAddJobStakeholder } = useCreateAddJobStakeholder();
  const { mutate: createAddStakeholder, isPending: isCreatePending } =
    useCreateAddStakeholder();

  // Read
  const { data: jobStakeholders, isPending, isError } = useGetJobStakeholders();
  const { data: allStakeholders } = useGetStakeholders();

  // Delete
  const { mutate: deleteJobStakeholder } = useDeleteRemoveJobStakeholder();

  /* React Hooks */
  // useState
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);

  /* Handlers */
  const handleAddExisting = (stakeholderId: number) => {
    const stakeholder = allStakeholders?.find((s) => s.id === stakeholderId);
    createAddJobStakeholder(
      { stakeholderId, stakeholder },
      {
        onSuccess: (addedStakeholder) => {
          setSelectedId(addedStakeholder.id);
          setIsEditing(true);
        },
      },
    );
    setDialogOpen(false);
  };

  const handleCreateNew = () => {
    createAddStakeholder(undefined, {
      onSuccess: (newStakeholder) => {
        createAddJobStakeholder(
          { stakeholderId: newStakeholder.id, stakeholder: newStakeholder },
          {
            onSuccess: (addedStakeholder) => {
              setSelectedId(addedStakeholder.id);
              setIsEditing(true);
            },
          },
        );
      },
    });
    setDialogOpen(false);
  };

  const handleRemove = (stakeholderId: number, remaining: Stakeholder[]) => {
    deleteJobStakeholder(
      { stakeholderId },
      {
        onSuccess: () => {
          if (selectedId === stakeholderId) {
            const next = remaining.find((s) => s.id !== stakeholderId);
            setSelectedId(next?.id);
          }
        },
      },
    );
  };

  const render = () => {
    if (isPending) return <LoadingIndicator variant="medium" />;

    if (isError) {
      return (
        <Column className="h-full items-center justify-center">
          <ErrorText errorMessage="An error occurred while loading stakeholders." />
        </Column>
      );
    }

    const linkedIds = new Set(jobStakeholders.map((s) => s.id));
    const unlinkedStakeholders = (allStakeholders ?? [])
      .filter((s) => !linkedIds.has(s.id))
      .sort((a, b) => {
        const nameA = `${a.firstName ?? ""} ${a.lastName ?? ""}`
          .trim()
          .toLowerCase();
        const nameB = `${b.firstName ?? ""} ${b.lastName ?? ""}`
          .trim()
          .toLowerCase();
        return nameA.localeCompare(nameB);
      });

    if (!jobStakeholders.length) {
      return (
        <Column className="gap-6">
          <EmptyState
            title="No stakeholders yet"
            description="Link a company stakeholder to this job."
            handlePrimaryClick={() => {
              setDialogOpen(true);
            }}
            Icon={UsersIcon}
            IsLoading={isCreatePending}
            primaryButtonName="Add stakeholder"
          />
          <AddJobStakeholderDialog
            isCreatePending={isCreatePending}
            onAddExisting={handleAddExisting}
            onCreateNew={handleCreateNew}
            onOpenChange={setDialogOpen}
            open={dialogOpen}
            unlinkedStakeholders={unlinkedStakeholders}
          />
        </Column>
      );
    }

    const resolvedSelectedId = selectedId ?? jobStakeholders[0]?.id;
    const selectedStakeholder = jobStakeholders.find(
      (s) => s.id === resolvedSelectedId,
    );

    return (
      <Column className="gap-4">
        {/* top offset = combined height of IntakeHeader */}
        <Column className="sticky top-[8.78rem] z-10 bg-white">
          <IntakeTabBar
            addLabel="Add Stakeholder"
            deleteDescription="This will unlink the stakeholder from this job (the stakeholder itself will not be deleted)."
            onAdd={() => {
              setDialogOpen(true);
            }}
            onDelete={() => {
              handleRemove(resolvedSelectedId, jobStakeholders);
            }}
            screenName={MIXPANEL_SCREENS.JOB}
          >
            {jobStakeholders.map((stakeholder, index) => (
              <IntakeTab
                key={stakeholder.id}
                progress={
                  progressByStakeholderId[stakeholder.id] ?? EMPTY_PROGRESS
                }
                isSelected={resolvedSelectedId === stakeholder.id}
                label={
                  `${stakeholder.firstName ?? ""} ${stakeholder.lastName ?? ""}`.trim() ||
                  `Stakeholder ${String(index + 1)}`
                }
                onSelect={() => {
                  setSelectedId(stakeholder.id);
                }}
              />
            ))}
          </IntakeTabBar>
        </Column>
        {selectedStakeholder &&
          (isEditing ? (
            <StakeholderForm
              key={selectedStakeholder.id}
              stakeholder={selectedStakeholder}
            />
          ) : (
            <StakeholderReadOnly stakeholder={selectedStakeholder} />
          ))}
        <AddJobStakeholderDialog
          isCreatePending={isCreatePending}
          onAddExisting={handleAddExisting}
          onCreateNew={handleCreateNew}
          onOpenChange={setDialogOpen}
          open={dialogOpen}
          unlinkedStakeholders={unlinkedStakeholders}
        />
      </Column>
    );
  };

  return render();
};

const AddJobStakeholderDialog = ({
  isCreatePending,
  onAddExisting,
  onCreateNew,
  onOpenChange,
  open,
  unlinkedStakeholders,
}: {
  isCreatePending: boolean;
  onAddExisting: (stakeholderId: number) => void;
  onCreateNew: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  unlinkedStakeholders: Stakeholder[];
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-md">
      <DialogHeader className="px-6 pb-3 pt-6">
        <DialogTitle className="text-xl font-semibold text-slate-700">
          Add Stakeholder
        </DialogTitle>
        <DialogDescription className="text-sm text-slate-500">
          Search for an existing company stakeholder or create a new one.
        </DialogDescription>
      </DialogHeader>
      <Command>
        <div className="border-t border-slate-100 px-3 py-2">
          <CommandInput
            className="text-slate-700"
            placeholder="Search stakeholders..."
          />
        </div>
        <CommandList className="max-h-64">
          <CommandEmpty className="py-8 text-center text-sm text-slate-400">
            No stakeholders found.
          </CommandEmpty>
          {unlinkedStakeholders.length > 0 && (
            <CommandGroup heading="Company stakeholders">
              {unlinkedStakeholders.map((s) => {
                const name =
                  `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || "-";
                return (
                  <CommandItem
                    key={s.id}
                    onSelect={() => {
                      onAddExisting(s.id);
                    }}
                    value={name}
                  >
                    <Row className="h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100">
                      <UserIcon className="h-3.5 w-3.5 text-slate-500" />
                    </Row>
                    <span className="text-slate-700">{name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
        </CommandList>
        <div className="border-t border-slate-100">
          <CommandGroup>
            <CommandItem disabled={isCreatePending} onSelect={onCreateNew}>
              <Row className="h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50">
                <PlusIcon className="h-3.5 w-3.5 text-primary-700" />
              </Row>
              <span className="text-primary-700">Create new stakeholder</span>
            </CommandItem>
          </CommandGroup>
        </div>
      </Command>
    </DialogContent>
  </Dialog>
);
